import { useEffect, useRef, useState } from 'react';
import type { Monaco } from '@monaco-editor/react';
import { CodeFile, ParsingHelper } from 'openscad-parser';
import { preprocessScadModifiers } from '@/lib/scad-preprocessor';

/**
 * Marker owner tag for parser-based diagnostics.
 * Separate from the WASM compilation markers which use 'owner'.
 */
const PARSER_MARKER_OWNER = 'openscad-parser';

/**
 * Debounce delay in ms before parsing triggers after the user stops typing.
 */
const PARSE_DEBOUNCE_MS = 300;

/**
 * Hook that runs the openscad-parser on the current editor content
 * and sets Monaco editor markers for any syntax errors found.
 *
 * This provides realtime error checking as the user types, without
 * needing to wait for the full WASM compilation cycle.
 */
export function useScadParser(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editor: any | null,
  monaco: Monaco | null,
  code: string,
): { hasSyntaxError: boolean } {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hasSyntaxError, setHasSyntaxError] = useState(false);

  useEffect(() => {
    if (!editor || !monaco) return;

    // Clear any pending debounce
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      const model = editor.getModel();
      if (!model) return;

      try {
        // Preprocess custom modifiers (% and ~) before parsing so the
        // openscad-parser doesn't flag them as unknown syntax errors.
        const preprocessedCode = preprocessScadModifiers(code);
        const codeFile = new CodeFile('input.scad', preprocessedCode);
        const [, errorCollector] = ParsingHelper.parseFile(codeFile);

        if (errorCollector.errors.length === 0) {
          // No parse errors — clear parser markers
          monaco.editor.setModelMarkers(model, PARSER_MARKER_OWNER, []);
          setHasSyntaxError(false);
          return;
        }

        const markers = errorCollector.errors
          .map((err) => {
            const loc = err.codeLocation;
            if (!loc) return null;

            // CodeLocation uses 0-indexed line/col; Monaco uses 1-indexed
            const lineNumber = loc.line + 1;
            const column = loc.col + 1;

            // Validate line number is within model bounds
            if (lineNumber < 1 || lineNumber > model.getLineCount()) {
              return null;
            }

            return {
              startLineNumber: lineNumber,
              startColumn: column,
              endLineNumber: lineNumber,
              endColumn: model.getLineMaxColumn(lineNumber),
              message: err.message,
              severity: monaco.MarkerSeverity.Error,
            };
          })
          .filter((m): m is NonNullable<typeof m> => m !== null);

        setHasSyntaxError(true);
        monaco.editor.setModelMarkers(model, PARSER_MARKER_OWNER, markers);
      } catch {
        // If the parser itself throws (unexpected), clear markers silently
        const model = editor.getModel();
        if (model) {
          monaco.editor.setModelMarkers(model, PARSER_MARKER_OWNER, []);
        }
        setHasSyntaxError(false);
      }
    }, PARSE_DEBOUNCE_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [editor, monaco, code]);

  return { hasSyntaxError };
}
